import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'

export default function UpdateInventory() {
  const [parts, setParts] = useState([])
  const [loading, setLoading] = useState(true)
  const [updating, setUpdating] = useState({}) // Track which parts are being updated

  useEffect(() => {
    fetchParts()
  }, [])

  const fetchParts = () => {
    fetch('/api/parts')
      .then(r => r.json())
      .then(data => {
        setParts(data)
        setLoading(false)
      })
      .catch(err => {
        console.error('Error loading parts:', err)
        setLoading(false)
      })
  }

  const updateQuantity = async (partId, newQuantity) => {
    // Don't allow negative quantities
    if (newQuantity < 0) return

    // Mark as updating
    setUpdating(prev => ({ ...prev, [partId]: true }))

    // Optimistically update the UI
    setParts(prev => prev.map(part =>
      part.id === partId
        ? { ...part, quantity_in_stock: newQuantity }
        : part
    ))

    try {
      const response = await fetch(`/api/parts/${partId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ part: { quantity_in_stock: newQuantity } })
      })

      if (!response.ok) {
        // Revert on error
        fetchParts()
      }
    } catch (err) {
      console.error('Error updating quantity:', err)
      // Revert on error
      fetchParts()
    } finally {
      setUpdating(prev => ({ ...prev, [partId]: false }))
    }
  }

  const increment = (part) => {
    updateQuantity(part.id, part.quantity_in_stock + 1)
  }

  const decrement = (part) => {
    updateQuantity(part.id, part.quantity_in_stock - 1)
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    )
  }

  return (
    <div className="px-2 sm:px-4 max-w-2xl mx-auto">
      <div className="mb-4 flex items-center justify-between">
        <h1 className="text-xl font-bold text-gray-900">Update Inventory</h1>
        <Link
          to="/inventory"
          className="text-sm text-gray-600 hover:text-gray-900"
        >
          &larr; Back
        </Link>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        {/* Header */}
        <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 border-b text-xs font-medium text-gray-500 uppercase">
          <div className="col-span-5">Part</div>
          <div className="col-span-3 text-center">Image</div>
          <div className="col-span-4 text-center">Qty</div>
        </div>

        {/* Parts List */}
        <div className="divide-y divide-gray-200">
          {parts.map(part => (
            <div
              key={part.id}
              className={`grid grid-cols-12 gap-2 px-3 py-3 items-center ${
                part.needs_reorder ? 'bg-red-50' : ''
              }`}
            >
              {/* Part Name */}
              <div className="col-span-5">
                <div className="text-sm font-medium text-gray-900 line-clamp-2">
                  {part.description}
                </div>
              </div>

              {/* Image */}
              <div className="col-span-3 flex justify-center">
                {part.photo_thumbnail_url ? (
                  <img
                    src={part.photo_thumbnail_url}
                    alt={part.description}
                    className="h-12 w-12 object-cover rounded"
                  />
                ) : (
                  <div className="h-12 w-12 bg-gray-100 rounded flex items-center justify-center">
                    <svg className="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  </div>
                )}
              </div>

              {/* Quantity Controls */}
              <div className="col-span-4 flex items-center justify-center space-x-1">
                {/* Decrement Button */}
                <button
                  onClick={() => decrement(part)}
                  disabled={part.quantity_in_stock <= 0 || updating[part.id]}
                  className={`w-12 h-12 flex items-center justify-center rounded-lg text-2xl font-bold transition-colors ${
                    part.quantity_in_stock <= 0 || updating[part.id]
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-red-100 text-red-600 hover:bg-red-200 active:bg-red-300'
                  }`}
                  aria-label="Decrease quantity"
                >
                  -
                </button>

                {/* Quantity Display */}
                <div className={`w-12 h-12 flex items-center justify-center text-lg font-bold rounded-lg ${
                  updating[part.id] ? 'bg-gray-100 text-gray-400' : 'bg-gray-50 text-gray-900'
                }`}>
                  {updating[part.id] ? (
                    <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    part.quantity_in_stock
                  )}
                </div>

                {/* Increment Button */}
                <button
                  onClick={() => increment(part)}
                  disabled={updating[part.id]}
                  className={`w-12 h-12 flex items-center justify-center rounded-lg text-2xl font-bold transition-colors ${
                    updating[part.id]
                      ? 'bg-gray-100 text-gray-300 cursor-not-allowed'
                      : 'bg-green-100 text-green-600 hover:bg-green-200 active:bg-green-300'
                  }`}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
            </div>
          ))}
        </div>

        {parts.length === 0 && (
          <div className="px-4 py-8 text-center text-gray-500">
            No parts found. <Link to="/parts/new" className="text-blue-600 hover:underline">Add some parts</Link> first.
          </div>
        )}
      </div>
    </div>
  )
}
